import React, { FormEvent, ReactElement, KeyboardEvent } from 'react';
import classNames from 'classnames';
import InputMask from 'react-input-mask';
import uniqueId from 'lodash/uniqueId';

// Utils
import { KEY_CODES } from '../../../utils/constants';
import ThemeMode from '@/types/theme-mode';

export interface Props {
  onValueChange: (value: string) => void;
  onChange: (event: FormEvent<HTMLInputElement>) => void;
  onFocus: (event: React.FocusEvent) => void;
  onBlur: (event?: React.FocusEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onClick: (event: React.MouseEvent) => void;
  label?: string | JSX.Element;
  link?: JSX.Element;
  placeholder?: string;
  defaultValue?: string;
  helper?: string;
  className?: string;
  type?: 'text' | 'number' | 'password' | 'email' | 'search';
  success?: boolean;
  warning?: boolean;
  error?: boolean;
  disabled?: boolean;
  compact?: boolean;
  leftSection?: ReactElement;
  rightSection?: ReactElement;
  maxLength?: number;
  dark?: boolean;
  theme?: ThemeMode;
  name?: string;
  value?: string | number | string[] | null;
  required?: boolean;
  inputMask?: string;
  min?: number;
  max?: number;
  step?: number;
  autoFocus?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  invert?: boolean;
  hasHiddenLabel?: boolean;
  id?: string;
  controlled?: boolean; // when true, the value is controlled by the parent, when false it is controlled internally. defaults to true.
  role?: string; // the ARIA role of the input element
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-autocomplete'?: string;
}

interface State {
  focused: boolean;
}

class Input extends React.Component<Props, State> {
  inputContainer = React.createRef<HTMLDivElement>();

  private input = React.createRef<HTMLInputElement>();

  private id: string;

  static defaultProps = {
    type: 'text',
    onChange: () => {},
    onValueChange: () => {},
    onFocus: () => {},
    onBlur: () => {},
    onSubmit: () => {},
    onClick: () => {},
    placeholder: '',
    helper: '',
    label: '',
    success: false,
    warning: false,
    error: false,
    disabled: false,
    leftSection: null,
    rightSection: null,
    hasHiddenLabel: false,
    controlled: true,
  };

  readonly state: State = {
    focused: false,
  };

  constructor(props: Props) {
    super(props);

    this.id = props.id ? props.id : uniqueId('input-');
  }

  componentDidMount() {
    const { value: propValue, controlled } = this.props;
    if (!controlled) {
      const inputValue = this.input.current && this.input.current.value;

      if (inputValue !== propValue) {
        this.setInputValue(this.props.value as string, true);
      }
    }

    if (this.props.autoFocus) {
      this.focus();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { value: propValue, controlled } = this.props;
    if (!controlled) {
      const { value: prevValue } = prevProps;
      const inputValue = this.input.current && this.input.current.value;

      if (inputValue !== propValue && prevValue !== propValue) {
        this.setInputValue(this.props.value as string, true);
      }
    }
  }

  public modifyInput(fn: (value: string) => string) {
    if (this.input.current) {
      this.setInputValue(fn(this.input.current.value));
    }
  }

  getInputValue = () => (this.input.current ? this.input.current.value : '');

  setInputValue = (value: string, bypassOnChange: boolean = false) => {
    if (this.input.current) {
      this.input.current.value = value;

      if (!bypassOnChange) {
        this.props.onValueChange(value);
      }
    }
  };

  focus = () => {
    if (this.input.current) {
      this.input.current.focus();
    }
  };

  blur = () => {
    if (this.input.current) {
      this.input.current.blur();
    }
  };

  selectAll = () => {
    if (this.input.current) {
      this.input.current.focus();
      this.input.current.select();
    }
  };

  onChange = (event: FormEvent<HTMLInputElement>) => {
    if (this.props.disabled) {
      return;
    }

    this.props.onChange(event);
    this.props.onValueChange(event.currentTarget.value);
  };

  handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (this.props.min != null && this.props.min >= 0 && e.key === KEY_CODES.MINUS && this.props.type === 'number') {
      e.preventDefault();
    }
    if (e.key === KEY_CODES.ENTER) {
      this.props.onSubmit();
    }
  };

  onClick = () => {
    this.focus();
  };

  onFocus = (event: React.FocusEvent) => {
    this.props.onFocus(event);
    this.setState({ focused: true }, () => {
      this.props.onFocus(event);
    });
  };

  onBlur = () => {
    this.setState({ focused: false }, () => {
      this.props.onBlur();
    });
  };

  render() {
    const { compact, dark, theme, disabled, error, warning, success, inputMask, value, invert, controlled, ...rest } = this.props;
    const { label, link, leftSection, rightSection, helper, required, onValueChange, className, hasHiddenLabel, id, ...props } = rest;

    const classes = classNames('input', this.props.className, {
      compact,
      dark,
      [`${theme}`]: theme,
      disabled,
      error,
      warning,
      success,
      invert,
      focused: this.state.focused,
      'has-right-section': rightSection,
      'has-left-section': leftSection,
    });

    return (
      <div className={classes} onClick={this.onClick}>
        {(label || link) && (
          <div className={classNames('label-container', { 'visually-hidden': hasHiddenLabel })}>
            <label htmlFor={this.id}>
              {label}
              {required ? '*' : null}
            </label>

            {link && <label htmlFor={this.id}>{link}</label>}
          </div>
        )}

        <div className="input-container d-flex flex-cell" ref={this.inputContainer} onClick={props.onClick}>
          {leftSection && <div className="left-section d-flex flex-column justify-content-center">{leftSection}</div>}

          {inputMask && (
            <InputMask
              mask={inputMask}
              value={value}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              name={this.props.name}
              disabled={disabled}
            >
              {(inputProps: any) => (
                <input
                  ref={this.input}
                  id={this.id}
                  autoCorrect="off"
                  autoFocus={this.props.autoFocus}
                  onKeyPress={this.handleKeyPress}
                  placeholder={props.placeholder}
                  maxLength={this.props.maxLength}
                  value={controlled ? value : undefined}
                  aria-expanded={this.props['aria-expanded']}
                  aria-controls={this.props['aria-controls']}
                  aria-autocomplete={this.props['aria-autocomplete']}
                  role={this.props.role}
                  {...inputProps}
                />
              )}
            </InputMask>
          )}

          {!inputMask && (
            <input
              {...props}
              id={this.id}
              ref={this.input}
              disabled={disabled}
              autoFocus={this.props.autoFocus}
              autoCorrect="off"
              spellCheck={false}
              onKeyPress={this.handleKeyPress}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              maxLength={this.props.maxLength}
              value={controlled ? value : undefined}
              role={this.props.role}
              aria-expanded={this.props['aria-expanded']}
              aria-controls={this.props['aria-controls']}
              aria-autocomplete={this.props['aria-autocomplete']}
            />
          )}

          {rightSection && <div className="right-section d-flex flex-column justify-content-center">{rightSection}</div>}
        </div>

        {helper && <span className="input-helper-text">{helper}</span>}
      </div>
    );
  }
}

export default Input;
